import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { logError } from '../utils/log';

const gameStructure = [
    { name: 'Game 1: 301 Singles', type: 'S301' },
    { name: 'Game 2: 301 Singles', type: 'S301' },
    { name: 'Game 3: 301 Doubles', type: 'D301' },
    { name: 'Game 4: 501 Singles', type: 'S501' },
    { name: 'Game 5: 501 Singles', type: 'S501' },
    { name: 'Game 6: 501 Doubles', type: 'D501' },
    { name: 'Game 7: Cricket Singles', type: 'SCricket' },
    { name: 'Game 8: Cricket Singles', type: 'SCricket' },
    { name: 'Game 9: Cricket Doubles', type: 'DCricket' },
];

export const UploadMatchResultsPage = ({ userRole }) => {
    const [schedules, setSchedules] = useState([]);
    const [teams, setTeams] = useState([]);
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false); // For mock data generation
    const [message, setMessage] = useState('');

    const [selectedScheduleId, setSelectedScheduleId] = useState('');
    const [selectedWeekIndex, setSelectedWeekIndex] = useState('');
    const [selectedGameIndex, setSelectedGameIndex] = useState('');

    const [gameScores, setGameScores] = useState(Array(9).fill({ home: 0, away: 0 }));
    const [playerAssignments, setPlayerAssignments] = useState({});
    const [highlights, setHighlights] = useState({});

    useEffect(() => {
        if (userRole !== 'admin') return;
        const fetchData = async () => {
            setLoading(true);
            try {
                const schedulesSnapshot = await getDocs(collection(db, "schedules"));
                setSchedules(schedulesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

                const teamsSnapshot = await getDocs(collection(db, "teams"));
                setTeams(teamsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

                const playersSnapshot = await getDocs(collection(db, "users"));
                setPlayers(playersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (error) {
                logError("Error fetching data: ", error);
                setMessage("Failed to load data.");
            }
            setLoading(false);
        };
        fetchData();
    }, [userRole]);

    const selectedSchedule = schedules.find(s => s.id === selectedScheduleId);
    const selectedWeek = selectedSchedule?.weeks[selectedWeekIndex];
    const selectedGame = selectedWeek?.games[selectedGameIndex];

    const homeTeam = teams.find(t => t.id === selectedGame?.homeTeamId);
    const awayTeam = teams.find(t => t.id === selectedGame?.awayTeamId);

    const homeTeamRoster = homeTeam?.members.map(id => players.find(p => p.id === id)).filter(Boolean) || [];
    const awayTeamRoster = awayTeam?.members.map(id => players.find(p => p.id === id)).filter(Boolean) || [];

    // Effect to load existing data when a match is selected
    useEffect(() => {
        if (selectedGame?.results) {
            setGameScores(selectedGame.results.gameScores || Array(9).fill({ home: 0, away: 0 }));
            setPlayerAssignments(selectedGame.results.playerAssignments || {});
            setHighlights(selectedGame.results.highlights || {});
            setMessage("Loaded existing results for this match. You can now edit and re-save.");
        } else {
            // Reset forms if no data exists
            setGameScores(Array(9).fill({ home: 0, away: 0 }));
            setPlayerAssignments({});
            setHighlights({});
        }
    }, [selectedGame]);

    // Auto-assign players for doubles games if a team only has 2 players
    useEffect(() => {
        const autoAssignDoubles = (teamRoster, teamType) => {
            if (teamRoster.length !== 2) return;

            const [player1, player2] = teamRoster;
            const newAssignments = {};

            gameStructure.forEach((game, idx) => {
                if (game.type.startsWith('D')) {
                    newAssignments[`${idx}-${teamType}-p1`] = player1.id;
                    newAssignments[`${idx}-${teamType}-p2`] = player2.id;
                }
            });

            setPlayerAssignments(prev => ({ ...prev, ...newAssignments }));
        };

        autoAssignDoubles(homeTeamRoster, 'home');
        autoAssignDoubles(awayTeamRoster, 'away');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [homeTeamRoster.length, awayTeamRoster.length, selectedGame]);

    // Auto-assign players for paired singles games if a team only has 2 players
    useEffect(() => {
        const autoAssign = (teamRoster, teamType) => {
            if (teamRoster.length !== 2) return;

            const [player1, player2] = teamRoster;
            const pairedGames = [[0, 1], [3, 4], [6, 7]]; // 301, 501, Cricket singles pairs

            pairedGames.forEach(pair => {
                const [game1Idx, game2Idx] = pair;
                const game1PlayerKey = `${game1Idx}-${teamType}-p1`;
                const game2PlayerKey = `${game2Idx}-${teamType}-p1`;

                const game1Assignment = playerAssignments[game1PlayerKey];
                const game2Assignment = playerAssignments[game2PlayerKey];

                if (game1Assignment && !game2Assignment) {
                    // If game 1 is assigned, assign game 2 to the other player
                    const otherPlayerId = game1Assignment === player1.id ? player2.id : player1.id;
                    setPlayerAssignments(prev => ({ ...prev, [game2PlayerKey]: otherPlayerId }));
                } else if (!game1Assignment && game2Assignment) {
                    // If game 2 is assigned, assign game 1 to the other player
                    const otherPlayerId = game2Assignment === player1.id ? player2.id : player1.id;
                    setPlayerAssignments(prev => ({ ...prev, [game1PlayerKey]: otherPlayerId }));
                }
            });
        };

        autoAssign(homeTeamRoster, 'home');
        autoAssign(awayTeamRoster, 'away');

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playerAssignments, homeTeamRoster.length, awayTeamRoster.length]);


    const handleScoreChange = (gameIdx, team, score) => {
        const newScores = [...gameScores];
        const newScore = parseInt(score, 10);
        if (team === 'home') {
            newScores[gameIdx] = { ...newScores[gameIdx], home: newScore };
            if (newScore === 2) newScores[gameIdx].away = Math.min(newScores[gameIdx].away, 1);
        } else {
            newScores[gameIdx] = { ...newScores[gameIdx], away: newScore };
            if (newScore === 2) newScores[gameIdx].home = Math.min(newScores[gameIdx].home, 1);
        }
        setGameScores(newScores);
    };

    const handlePlayerAssignment = (gameIdx, team, playerType, playerId) => {
        setPlayerAssignments(prev => ({
            ...prev,
            [`${gameIdx}-${team}-${playerType}`]: playerId
        }));
    };

    const handleHighlightChange = (playerId, highlightType, value) => {
        setHighlights(prev => ({
            ...prev,
            [playerId]: {
                ...prev[playerId],
                [highlightType]: value
            }
        }));
    };

    // --- MOCK DATA GENERATION ---
    const generateMockDataForMatch = (gameData, homeRoster, awayRoster) => {
        // Helper to shuffle an array randomly
        const shuffle = (array) => array.sort(() => Math.random() - 0.5);

        const mockAssignments = {};
        const mockScores = [];
        const mockHighlights = {};
        const allPlayersInMatch = new Set();

        // --- Smart Player Assignment for Mock Data ---
        const assignPlayersForTeam = (roster, teamType) => {
            if (roster.length === 0) return;
            const shuffledRoster = shuffle([...roster]);

            // Assign for paired singles (301, 501, Cricket)
            const singlesPairs = [[0, 1], [3, 4], [6, 7]];
            singlesPairs.forEach(pair => {
                const p1 = shuffledRoster[0];
                const p2 = shuffledRoster.length > 1 ? shuffledRoster[1] : p1; // Use same player if only one
                mockAssignments[`${pair[0]}-${teamType}-p1`] = p1.id;
                mockAssignments[`${pair[1]}-${teamType}-p1`] = p2.id;
                allPlayersInMatch.add(p1.id);
                allPlayersInMatch.add(p2.id);
            });

            // Assign for doubles
            const doublesGames = [2, 5, 8];
            doublesGames.forEach(idx => {
                const p1 = shuffledRoster[0];
                const p2 = shuffledRoster.length > 1 ? shuffledRoster[1] : p1;
                mockAssignments[`${idx}-${teamType}-p1`] = p1.id;
                mockAssignments[`${idx}-${teamType}-p2`] = p2.id;
                allPlayersInMatch.add(p1.id);
                allPlayersInMatch.add(p2.id);
            });
        };

        assignPlayersForTeam(homeRoster, 'home');
        assignPlayersForTeam(awayRoster, 'away');

        // --- Generate Scores and Highlights ---
        gameStructure.forEach((gameDef, idx) => {
            // Generate scores with a slight home-team advantage for realism
            const homeWins = Math.random() > 0.45; // ~55% chance for home to win a game
            const loserScore = Math.random() > 0.5 ? 1 : 0;
            mockScores[idx] = homeWins ? { home: 2, away: loserScore } : { home: loserScore, away: 2 };
        });

        // Generate highlights for all involved players
        allPlayersInMatch.forEach(playerId => {
            const tons = Array.from({ length: Math.floor(Math.random() * 4) }, () => Math.floor(100 + Math.random() * 40));
            const highOuts = Math.random() > 0.9 ? [Math.floor(80 + Math.random() * 60)] : [];

            mockHighlights[playerId] = {
                tons: tons.join(','),
                on80: Math.random() > 0.9 ? Math.floor(80 + Math.random() * 20).toString() : '',
                highOut: highOuts.join(','),
                '180s': Math.random() > 0.85 ? '1' : '0',
                c6: Math.floor(Math.random() * 3).toString(),
                c7: Math.floor(Math.random() * 2).toString(),
                c8: Math.floor(Math.random() * 1.5).toString(),
                c9: Math.random() > 0.9 ? '1' : '0',
                deadeye: '0',
            };
        });

        // Calculate match stats from mock scores
        const matchStats = { home: { gamePoints: 0 }, away: { gamePoints: 0 } };
        mockScores.forEach(score => {
            if (score.home > score.away) matchStats.home.gamePoints++;
            else matchStats.away.gamePoints++;
        });

        return {
            gameScores: mockScores,
            playerAssignments: mockAssignments,
            highlights: mockHighlights,
            matchStats,
        };
    };

    const handleGenerateMockForSelected = async () => {
        if (!selectedGame) {
            setMessage("Please select a match first.");
            return;
        }
        setIsGenerating(true);
        setMessage("Generating mock data for the selected match...");
        const mockData = generateMockDataForMatch(selectedGame, homeTeamRoster, awayTeamRoster);
        
        // Use the generated data to update state and then save
        setGameScores(mockData.gameScores);
        setPlayerAssignments(mockData.playerAssignments);
        setHighlights(mockData.highlights);

        // This will trigger the main handleSubmit function with the mock data
        await handleSubmit(new Event('submit'), mockData);
        setIsGenerating(false);
    };

    const handleGenerateMockForSeason = async () => {
        if (!selectedSchedule) {
            setMessage("Please select a schedule first.");
            return;
        }
        if (!window.confirm("Are you sure you want to generate mock results for ALL matches in this entire season? This will overwrite any existing results.")) {
            return;
        }

        setIsGenerating(true);
        setMessage("Generating mock data for the entire season... This may take a while.");

        const updatedWeeks = JSON.parse(JSON.stringify(selectedSchedule.weeks));

        for (const week of updatedWeeks) {
            for (const game of week.games) {
                if (game.eventName) continue; // Skip events
                const hTeam = teams.find(t => t.id === game.homeTeamId);
                const aTeam = teams.find(t => t.id === game.awayTeamId);
                const hRoster = hTeam?.members.map(id => players.find(p => p.id === id)).filter(Boolean) || [];
                const aRoster = aTeam?.members.map(id => players.find(p => p.id === id)).filter(Boolean) || [];

                const mockData = generateMockDataForMatch(game, hRoster, aRoster);
                game.results = mockData;
                game.result = `${mockData.matchStats.home.gamePoints} - ${mockData.matchStats.away.gamePoints}`;
            }
        }

        const scheduleDocRef = doc(db, "schedules", selectedScheduleId);
        try {
            await updateDoc(scheduleDocRef, { weeks: updatedWeeks });
            setMessage("Successfully generated mock data for the entire season!");
        } catch (error) {
            setMessage("Error generating full season mock data.");
            logError("Error updating schedule with mock data: ", error);
        }
        setIsGenerating(false);
    };

    const getPlayerName = (playerId) => {
        const player = players.find(p => p.id === playerId);
        if (!player) return 'N/A';
        const hasNickname = player.nickname && player.nickname.trim() !== '';
        return hasNickname
            ? `${player.nickname} (${player.firstName} ${player.lastName})`
            : `${player.firstName} ${player.lastName}`;
    };

    const handleSubmit = async (e, directData = null) => {
        if (e) e.preventDefault();
        if (!selectedGame) {
            setMessage("Please select a match to enter results for.");
            return;
        }
        const dataToSave = directData || { gameScores, playerAssignments, highlights };

        // --- Score Validation ---
        for (const score of gameScores) {
            const totalScore = score.home + score.away;
            if (totalScore === 0 || (score.home < 2 && score.away < 2)) {
                setMessage(`Invalid score detected: ${score.home}-${score.away}. A game must have a winner with 2 legs.`);
                return;
            }
        }

        // --- Player Assignment Validation ---
        const pairedSinglesGames = [[0, 1], [3, 4], [6, 7]];
        for (const pair of pairedSinglesGames) {
            const [game1Idx, game2Idx] = pair;
            const homeP1 = playerAssignments[`${game1Idx}-home-p1`];
            const homeP2 = playerAssignments[`${game2Idx}-home-p1`];
            if (homeP1 && homeP2 && homeP1 === homeP2) {
                setMessage(`Validation Error: The same player cannot play both ${gameStructure[game1Idx].name} matches for the home team.`);
                return;
            }

            const awayP1 = playerAssignments[`${game1Idx}-away-p1`];
            const awayP2 = playerAssignments[`${game2Idx}-away-p1`];
            if (awayP1 && awayP2 && awayP1 === awayP2) {
                setMessage(`Validation Error: The same player cannot play both ${gameStructure[game1Idx].name} matches for the away team.`);
                return;
            }
        }


        // --- Pre-calculate stats for this match ---
        const matchStats = {
            home: { gamePoints: 0, S301: {legsWon: 0, legsPlayed: 0}, D301: {gamesWon: 0, gamesPlayed: 0}, S501: {legsWon: 0, legsPlayed: 0}, D501: {gamesWon: 0, gamesPlayed: 0}, SCricket: {legsWon: 0, legsPlayed: 0}, DCricket: {gamesWon: 0, gamesPlayed: 0} },
            away: { gamePoints: 0, S301: {legsWon: 0, legsPlayed: 0}, D301: {gamesWon: 0, gamesPlayed: 0}, S501: {legsWon: 0, legsPlayed: 0}, D501: {gamesWon: 0, gamesPlayed: 0}, SCricket: {legsWon: 0, legsPlayed: 0}, DCricket: {gamesWon: 0, gamesPlayed: 0} },
        };

        dataToSave.gameScores.forEach((score, idx) => {
            const gameType = gameStructure[idx].type;
            // Add legs won

            if (gameType.startsWith('S')) {
                // For singles, track legs won and played
                matchStats.home[gameType].legsWon += score.home;
                matchStats.away[gameType].legsWon += score.away;
                matchStats.home[gameType].legsPlayed += score.home + score.away;
                matchStats.away[gameType].legsPlayed += score.home + score.away;
            } else {
                // For doubles, track games won and played
                matchStats.home[gameType].gamesPlayed++;
                matchStats.away[gameType].gamesPlayed++;
            }

            // Tally game points (for overall match score) and doubles game wins
            if (score.home > score.away) {
                matchStats.home.gamePoints++;
                if (gameType.startsWith('D')) {
                    matchStats.home[gameType].gamesWon++;
                }
            } else if (score.away > score.home) {
                matchStats.away.gamePoints++;
                if (gameType.startsWith('D')) {
                    matchStats.away[gameType].gamesWon++;
                }
            }
        });
        // --- End Stat Calculation ---

        const detailedResults = {
            gameScores: dataToSave.gameScores,
            playerAssignments: dataToSave.playerAssignments,
            highlights: dataToSave.highlights,
            matchStats, // Embed the pre-calculated stats
        };

        const scheduleDocRef = doc(db, "schedules", selectedScheduleId);
        const newWeeks = [...selectedSchedule.weeks];
        
        newWeeks[selectedWeekIndex].games[selectedGameIndex].results = detailedResults; // Save detailed object
        newWeeks[selectedWeekIndex].games[selectedGameIndex].result = `${matchStats.home.gamePoints} - ${matchStats.away.gamePoints}`; // Use calculated game points for the result string
        
        try {
            await updateDoc(scheduleDocRef, { weeks: newWeeks });
            setMessage("Match result saved successfully!");

            // --- PLAYOFF ADVANCEMENT LOGIC ---
            if (selectedGame.playoffId && selectedSchedule.playoffBracket) {
                const winnerId = matchStats.home.gamePoints > matchStats.away.gamePoints ? selectedGame.homeTeamId : selectedGame.awayTeamId;
                
                // Find where this winner should go
                const allRounds = [
                    ...selectedSchedule.playoffBracket.round1, 
                    ...selectedSchedule.playoffBracket.round2, 
                    ...selectedSchedule.playoffBracket.finals
                ];

                for (const week of newWeeks) {
                    for (const game of week.games) {
                        if (game.advancement?.home?.from === selectedGame.playoffId) {
                            game.homeTeamId = winnerId;
                        }
                        if (game.advancement?.away?.from === selectedGame.playoffId) {
                            game.awayTeamId = winnerId;
                        }

                        // After potentially updating, check if both teams are now set and update venue
                        if (game.homeTeamId !== 'TBD' && game.awayTeamId !== 'TBD') {
                            const bracketMatch = allRounds.find(m => m.id === game.playoffId);
                            if (bracketMatch) {
                                // Find the original seed of the teams now in the home/away slots
                                const homeTeamOriginalSeed = selectedSchedule.playoffBracket.round1.find(m => m.home.teamId === game.homeTeamId || m.away.teamId === game.homeTeamId)?.home.seed;
                                const awayTeamOriginalSeed = selectedSchedule.playoffBracket.round1.find(m => m.home.teamId === game.awayTeamId || m.away.teamId === game.awayTeamId)?.home.seed;

                                // Higher seed (lower number) gets the home game. Default to current home team if seeds are not found.
                                const homeIsHigherSeed = (homeTeamOriginalSeed || 99) < (awayTeamOriginalSeed || 100);
                                const newHomeId = homeIsHigherSeed ? game.homeTeamId : game.awayTeamId;
                                const newAwayId = homeIsHigherSeed ? game.awayTeamId : game.homeTeamId;
                                game.homeTeamId = newHomeId;
                                game.awayTeamId = newAwayId;
                                
                                const newHomeTeam = teams.find(t => t.id === newHomeId);
                                if (newHomeTeam) game.venue = newHomeTeam.homeLocation;
                            }
                        }
                    }
                }
                // Save the newly advanced schedule
                await updateDoc(scheduleDocRef, { weeks: newWeeks });
            }
        } catch (error) {
            setMessage("Error saving match result.");
            logError("Error updating schedule: ", error);
        }
    };

    if (userRole !== 'admin') {
        return <p className="text-xl text-danger text-center">Access Denied. This page is for administrators only.</p>;
    }

    if (loading) {
        return <div className="text-center">Loading data...</div>;
    }

    return (
        <div className="space-y-6">
            <h2 className="page-title heading-primary-1">Enter Match Results</h2>
            {/* Temporary Mock Data Buttons */}
            <div className="card p-4 rounded-lg flex justify-center gap-4">
                <button type="button" onClick={handleGenerateMockForSelected} disabled={isGenerating || !selectedGame} className="btn btn-warning">
                    {isGenerating ? 'Working...' : 'Generate Mock for Selected Match'}
                </button>
                <button type="button" onClick={handleGenerateMockForSeason} disabled={isGenerating || !selectedSchedule} className="btn btn-danger">
                    {isGenerating ? 'Working...' : 'Generate Mock for Full Season'}
                </button>
            </div>
            {message && <p className="text-center text-success">{message}</p>}

            <form onSubmit={handleSubmit}>
                {/* Match Selection */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 card rounded-lg">
                    <select value={selectedScheduleId} onChange={e => setSelectedScheduleId(e.target.value)} className="select-field p-2">
                        <option value="">Select Schedule</option>
                        {schedules.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <select value={selectedWeekIndex} onChange={e => setSelectedWeekIndex(e.target.value)} className="select-field p-2" disabled={!selectedSchedule}>
                        <option value="">Select Week</option>
                        {selectedSchedule?.weeks.map((w, i) => <option key={i} value={i}>{w.week}</option>)}
                    </select>
                    <select value={selectedGameIndex} onChange={e => setSelectedGameIndex(e.target.value)} className="select-field p-2" disabled={!selectedWeek}>
                        <option value="">Select Match</option>
                        {selectedWeek?.games.map((g, i) => !g.eventName && <option key={i} value={i}>{teams.find(t=>t.id===g.awayTeamId)?.name} at {teams.find(t=>t.id===g.homeTeamId)?.name}</option>)}
                    </select>
                </div>

                {selectedGame && homeTeam && awayTeam && (
                    <>
                        {/* Game Scores Section */}
                        <div className="card p-6 rounded-lg shadow-md overflow-x-auto">
                            <h3 className="text-2xl font-bold mb-4">Game-by-Game Results</h3>
                            <table className="min-w-max sm:min-w-full">
                                <thead>
                                    <tr className="table-header">
                                        <th className="py-2 text-left">Game</th>
                                        <th className="py-2 text-left">{awayTeam.name} Player(s)</th>
                                        <th className="py-2 text-center">Score</th>
                                        <th className="py-2 text-left">{homeTeam.name} Player(s)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {gameStructure.map((game, idx) => (
                                        <tr key={idx} className="border-b">
                                            <td className="py-2 font-semibold">{game.name}</td>
                                            <td className="p-2">
                                                {game.type.startsWith('S') ? ( // Singles
                                                    <select value={playerAssignments[`${idx}-away-p1`] || ''} onChange={e => handlePlayerAssignment(idx, 'away', 'p1', e.target.value)} className="select-field p-1 w-full"><option value="">Select Player</option>{awayTeamRoster.map(p => <option key={p.id} value={p.id}>{getPlayerName(p.id)}</option>)}</select>
                                                ) : ( // Doubles
                                                    <div>
                                                        <select value={playerAssignments[`${idx}-away-p1`] || ''} onChange={e => handlePlayerAssignment(idx, 'away', 'p1', e.target.value)} className="select-field p-1 w-full mb-1"><option value="">Player 1</option>{awayTeamRoster.map(p => <option key={p.id} value={p.id}>{getPlayerName(p.id)}</option>)}</select>
                                                        <select value={playerAssignments[`${idx}-away-p2`] || ''} onChange={e => handlePlayerAssignment(idx, 'away', 'p2', e.target.value)} className="select-field p-1 w-full"><option value="">Player 2</option>{awayTeamRoster.filter(p => p.id !== playerAssignments[`${idx}-away-p1`]).map(p => <option key={p.id} value={p.id}>{getPlayerName(p.id)}</option>)}</select>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <div className="flex justify-center items-center gap-2">
                                                    <select value={gameScores[idx].away} onChange={e => handleScoreChange(idx, 'away', e.target.value)} className="select-field p-1 w-16">
                                                        <option value="0">0</option><option value="1">1</option><option value="2">2</option>
                                                    </select>
                                                    <span>-</span>
                                                    <select value={gameScores[idx].home} onChange={e => handleScoreChange(idx, 'home', e.target.value)} className="select-field p-1 w-16">
                                                        <option value="0">0</option><option value="1">1</option><option value="2" disabled={gameScores[idx].away === 2}>2</option>
                                                    </select>
                                                </div>
                                            </td>
                                            <td className="p-2">
                                                {game.type.startsWith('S') ? ( // Singles
                                                    <select value={playerAssignments[`${idx}-home-p1`] || ''} onChange={e => handlePlayerAssignment(idx, 'home', 'p1', e.target.value)} className="select-field p-1 w-full"><option value="">Select Player</option>{homeTeamRoster.map(p => <option key={p.id} value={p.id}>{getPlayerName(p.id)}</option>)}</select>
                                                ) : ( // Doubles
                                                    <div>
                                                        <select value={playerAssignments[`${idx}-home-p1`] || ''} onChange={e => handlePlayerAssignment(idx, 'home', 'p1', e.target.value)} className="select-field p-1 w-full mb-1"><option value="">Player 1</option>{homeTeamRoster.map(p => <option key={p.id} value={p.id}>{getPlayerName(p.id)}</option>)}</select>
                                                        <select value={playerAssignments[`${idx}-home-p2`] || ''} onChange={e => handlePlayerAssignment(idx, 'home', 'p2', e.target.value)} className="select-field p-1 w-full"><option value="">Player 2</option>{homeTeamRoster.filter(p => p.id !== playerAssignments[`${idx}-home-p1`]).map(p => <option key={p.id} value={p.id}>{getPlayerName(p.id)}</option>)}</select>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Highlights Section */}
                        <div className="card p-6 rounded-lg shadow-md mt-6 overflow-x-auto">
                            <h3 className="text-2xl font-bold mb-4">Player Highlights</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="text-xl font-semibold mb-2">{awayTeam.name}</h4>
                                    {awayTeamRoster.map(player => (
                                        <div key={player.id} className="mb-4 p-2 border rounded">
                                            <p className="font-bold subheading-primary-1">
                                                {player.nickname ? `${player.nickname} (${player.firstName} ${player.lastName})` : `${player.firstName} ${player.lastName}`}
                                            </p>
                                            <input type="text" value={highlights[player.id]?.tons || ''} placeholder="Tons (e.g., 100,121)" onChange={e => handleHighlightChange(player.id, 'tons', e.target.value)} className="input-field w-full p-1 mt-1" />
                                            <input type="text" value={highlights[player.id]?.on80 || ''} placeholder="80+ On" onChange={e => handleHighlightChange(player.id, 'on80', e.target.value)} className="input-field w-full p-1 mt-1" />
                                            <input type="text" value={highlights[player.id]?.highOut || ''} placeholder="High Out (e.g., 96, 115)" onChange={e => handleHighlightChange(player.id, 'highOut', e.target.value)} className="input-field w-full p-1 mt-1" />
                                            <input type="number" value={highlights[player.id]?.['180s'] || ''} placeholder="180s" onChange={e => handleHighlightChange(player.id, '180s', e.target.value)} className="input-field w-full p-1 mt-1" />
                                            <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                                                <label>6-Hits: <input type="number" value={highlights[player.id]?.c6 || ''} min="0" className="input-field w-12 p-1" onChange={e => handleHighlightChange(player.id, 'c6', e.target.value)} /></label>
                                                <label>7-Hits: <input type="number" value={highlights[player.id]?.c7 || ''} min="0" className="input-field w-12 p-1" onChange={e => handleHighlightChange(player.id, 'c7', e.target.value)} /></label>
                                                <label>8-Hits: <input type="number" value={highlights[player.id]?.c8 || ''} min="0" className="input-field w-12 p-1" onChange={e => handleHighlightChange(player.id, 'c8', e.target.value)} /></label>
                                                <label>9-Hits: <input type="number" value={highlights[player.id]?.c9 || ''} min="0" className="input-field w-12 p-1" onChange={e => handleHighlightChange(player.id, 'c9', e.target.value)} /></label>
                                                <label>Deadeyes: <input type="number" value={highlights[player.id]?.deadeye || ''} min="0" className="input-field w-12 p-1" onChange={e => handleHighlightChange(player.id, 'deadeye', e.target.value)} /></label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div>
                                    <h4 className="text-xl font-semibold mb-2">{homeTeam.name}</h4>
                                    {homeTeamRoster.map(player => (
                                        <div key={player.id} className="mb-4 p-2 border rounded">
                                            <p className="font-bold subheading-primary-1">
                                                {player.nickname ? `${player.nickname} (${player.firstName} ${player.lastName})` : `${player.firstName} ${player.lastName}`}
                                            </p>
                                            <input type="text" value={highlights[player.id]?.tons || ''} placeholder="Tons (e.g., 100,121)" onChange={e => handleHighlightChange(player.id, 'tons', e.target.value)} className="input-field w-full p-1 mt-1" />
                                            <input type="text" value={highlights[player.id]?.on80 || ''} placeholder="80+ On" onChange={e => handleHighlightChange(player.id, 'on80', e.target.value)} className="input-field w-full p-1 mt-1" />
                                            <input type="text" value={highlights[player.id]?.highOut || ''} placeholder="80+ Out (e.g., 96, 115)" onChange={e => handleHighlightChange(player.id, 'highOut', e.target.value)} className="input-field w-full p-1 mt-1" />
                                            <input type="number" value={highlights[player.id]?.['180s'] || ''} placeholder="180s" onChange={e => handleHighlightChange(player.id, '180s', e.target.value)} className="input-field w-full p-1 mt-1" />
                                            <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                                                <label>6-Hits: <input type="number" value={highlights[player.id]?.c6 || ''} min="0" className="input-field w-12 p-1" onChange={e => handleHighlightChange(player.id, 'c6', e.target.value)} /></label>
                                                <label>7-Hits: <input type="number" value={highlights[player.id]?.c7 || ''} min="0" className="input-field w-12 p-1" onChange={e => handleHighlightChange(player.id, 'c7', e.target.value)} /></label>
                                                <label>8-Hits: <input type="number" value={highlights[player.id]?.c8 || ''} min="0" className="input-field w-12 p-1" onChange={e => handleHighlightChange(player.id, 'c8', e.target.value)} /></label>
                                                <label>9-Hits: <input type="number" value={highlights[player.id]?.c9 || ''} min="0" className="input-field w-12 p-1" onChange={e => handleHighlightChange(player.id, 'c9', e.target.value)} /></label>
                                                <label>Deadeyes: <input type="number" value={highlights[player.id]?.deadeye || ''} min="0" className="input-field w-12 p-1" onChange={e => handleHighlightChange(player.id, 'deadeye', e.target.value)} /></label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Results Summary Section */}
                        <div className="card p-6 rounded-lg shadow-md mt-6 overflow-x-auto">
                            <h3 className="text-2xl font-bold mb-4">Results Summary</h3>
                            <table className="min-w-max sm:min-w-full text-sm">
                                <thead>
                                    <tr className="table-header">
                                        <th className="py-2 text-left">Game</th>
                                        <th className="py-2 text-left">{awayTeam.name}</th>
                                        <th className="py-2 text-center">Score</th>
                                        <th className="py-2 text-right">{homeTeam.name}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {gameStructure.map((game, idx) => {
                                        const homeP1 = getPlayerName(playerAssignments[`${idx}-home-p1`]);
                                        const homeP2 = getPlayerName(playerAssignments[`${idx}-home-p2`]);
                                        const awayP1 = getPlayerName(playerAssignments[`${idx}-away-p1`]);
                                        const awayP2 = getPlayerName(playerAssignments[`${idx}-away-p2`]);
                                        const homePlayers = game.type.startsWith('S') ? homeP1 : `${homeP1} & ${homeP2}`;
                                        const awayPlayers = game.type.startsWith('S') ? awayP1 : `${awayP1} & ${awayP2}`;
                                        return (
                                            <tr key={idx} className="border-b">
                                                <td className="py-2">{game.name}</td>
                                                <td className="py-2">{awayPlayers}</td>
                                                <td className="py-2 text-center font-bold">{gameScores[idx].away} - {gameScores[idx].home}</td>
                                                <td className="py-2 text-right">{homePlayers}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        
                        {/* Highlights Summary */}
                        <div className="card p-6 rounded-lg shadow-md mt-6">
                            <h3 className="text-2xl font-bold mb-4">Highlights Summary</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="text-xl font-semibold mb-2">{awayTeam.name}</h4>
                                    {awayTeamRoster.map(player => {
                                        const playerHighlights = highlights[player.id];
                                        if (!playerHighlights || Object.values(playerHighlights).every(v => !v)) return null;
                                        return <HighlightSummaryCard key={player.id} playerName={getPlayerName(player.id)} highlights={playerHighlights} />;
                                    })}
                                </div>
                                <div>
                                    <h4 className="text-xl font-semibold mb-2">{homeTeam.name}</h4>
                                    {homeTeamRoster.map(player => {
                                        const playerHighlights = highlights[player.id];
                                        if (!playerHighlights || Object.values(playerHighlights).every(v => !v)) return null;
                                        return <HighlightSummaryCard key={player.id} playerName={getPlayerName(player.id)} highlights={playerHighlights} />;
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="mt-6">
                            <button type="submit" className="w-full btn btn-success font-bold py-3 px-4 rounded-lg">
                                Save Match Results
                            </button>
                        </div>
                    </>
                )}
            </form>
        </div>
    );
};

const HighlightSummaryCard = ({ playerName, highlights }) => (
    <div className="mb-4 surface-low border border-low p-4 rounded-lg">
        <p className="font-bold text-lg subheading-primary-1">{playerName}</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-sm">
            {highlights.tons && <div className="flex justify-between"><span>Tons:</span> <span className="font-semibold">{highlights.tons}</span></div>}
            {highlights.on80 && <div className="flex justify-between"><span>80+ On:</span> <span className="font-semibold">{highlights.on80}</span></div>}
            {highlights.highOut && <div className="flex justify-between"><span>80+ Out:</span> <span className="font-semibold">{highlights.highOut}</span></div>}
            {highlights['180s'] > 0 && <div className="flex justify-between"><span>180s:</span> <span className="font-semibold">{highlights['180s']}</span></div>}
            {highlights.c6 > 0 && <div className="flex justify-between"><span>6-Hits:</span> <span className="font-semibold">{highlights.c6}</span></div>}
            {highlights.c7 > 0 && <div className="flex justify-between"><span>7-Hits:</span> <span className="font-semibold">{highlights.c7}</span></div>}
            {highlights.c8 > 0 && <div className="flex justify-between"><span>8-Hits:</span> <span className="font-semibold">{highlights.c8}</span></div>}
            {highlights.c9 > 0 && <div className="flex justify-between"><span>9-Hits:</span> <span className="font-semibold">{highlights.c9}</span></div>}
            {highlights.deadeye > 0 && <div className="flex justify-between"><span>Deadeyes:</span> <span className="font-semibold">{highlights.deadeye}</span></div>}
        </div>
    </div>
);